import { SEO } from "@/components/SEO";
import { Helmet } from "react-helmet-async";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Link } from "react-router-dom";
import { 
  DollarSign, 
  Check, 
  X, 
  Smartphone, 
  Search, 
  Image, 
  FileText, 
  Settings,
  Calendar,
  ArrowRight,
  Clock,
  RefreshCw
} from "lucide-react";

const faqItems = [
  {
    question: "How much does a lawn care website cost per month?",
    answer: "Monthly costs vary by option: DIY builders run $15–$50/month, freelancer maintenance is $50–$150/month, and subscription services like ours cost $150–$300/month for a fully managed site with hosting, updates, and SEO included."
  },
  {
    question: "Is it cheaper to build my own lawn care website?",
    answer: "DIY builders have the lowest upfront cost ($0–$500), but you'll spend 20–40+ hours learning and building. Factor in your hourly rate—if your time is worth $50/hour, that's $1,000–$2,000 in time alone, plus ongoing maintenance."
  },
  {
    question: "Why do lawn care websites cost more than other small business sites?",
    answer: "Lawn care sites need service area pages for local SEO, seasonal content updates, before/after galleries, review integration, and mobile-first design for customers searching on their phones. These features take expertise to build correctly."
  },
  {
    question: "What's included in a lawn care website subscription?",
    answer: "Our subscription includes custom design, hosting, SSL, unlimited edits, SEO-ready structure, mobile optimization, service pages, service area pages, contact forms, and ongoing support—no hidden fees or surprise charges."
  },
  {
    question: "Do I own my website with a subscription model?",
    answer: "You own all your content, images, and copy. If you cancel, we'll export everything for you. The subscription covers the ongoing hosting, maintenance, updates, and support that keep your site running and ranking."
  },
  {
    question: "How much should I budget for a professional lawn care website?",
    answer: "Budget $2,000–$5,000 for a quality one-time build, or $200–$500/month for a subscription. The subscription often makes more sense because it includes hosting, updates, and support—things you'd pay extra for with a one-time build."
  },
  {
    question: "Are cheap lawn care websites worth it?",
    answer: "Sub-$500 websites typically use generic templates, lack local SEO, and don't convert visitors to leads. You'll likely rebuild within 1–2 years. Investing in quality upfront (or monthly) saves money long-term."
  },
  {
    question: "What hidden costs come with lawn care websites?",
    answer: "Watch for: annual hosting ($100–$300), domain renewal ($15–$20), SSL certificates ($0–$100), plugin updates, security monitoring, and revision fees. Subscriptions typically bundle all of these into one predictable payment."
  },
  {
    question: "How long does it take to get a return on my website investment?",
    answer: "Most lawn care companies see ROI within 3–6 months. One new weekly mowing client ($150–$200/month) covers a website subscription. A single landscaping project ($2,000+) can pay for an entire year."
  },
  {
    question: "Can I start cheap and upgrade later?",
    answer: "Yes, but rebuilding costs more than building right the first time. If budget is tight, start with a subscription—you get professional quality immediately with predictable monthly costs and can scale services as you grow."
  }
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": "https://www.leafrushmarketing.com/lawn-care-website-cost/#faq",
  "mainEntity": faqItems.map(item => ({
    "@type": "Question",
    "name": item.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": item.answer
    }
  }))
};

const webPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": "https://www.leafrushmarketing.com/lawn-care-website-cost/#webpage",
  "url": "https://www.leafrushmarketing.com/lawn-care-website-cost/",
  "name": "Lawn Care Website Cost: 2024 Pricing Guide | Leaf Rush Marketing",
  "description": "How much does a lawn care website cost? Compare DIY, freelancer, agency, and subscription pricing. Real numbers for lawn care business owners.",
  "isPartOf": {
    "@id": "https://www.leafrushmarketing.com/#website"
  },
  "about": {
    "@id": "https://www.leafrushmarketing.com/#organization"
  },
  "dateModified": new Date().toISOString().split('T')[0]
};

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": "https://www.leafrushmarketing.com/#organization",
  "name": "Leaf Rush Marketing",
  "url": "https://www.leafrushmarketing.com",
  "logo": "https://www.leafrushmarketing.com/og-image.jpg",
  "sameAs": []
};

const LawnCareWebsiteCost = () => {
  const today = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
  return (
    <>
      <SEO 
        title="Lawn Care Website Cost: 2024 Pricing Guide"
        description="How much does a lawn care website cost? Compare DIY, freelancer, agency, and subscription pricing. Real numbers for lawn care business owners."
        canonical="https://www.leafrushmarketing.com/lawn-care-website-cost/"
        ogUrl="https://www.leafrushmarketing.com/lawn-care-website-cost/"
        ogImage="https://www.leafrushmarketing.com/og-image.jpg"
        keywords="lawn care website cost, lawn care website pricing, lawn care website price, lawn care website monthly cost, lawn care website subscription, how much does a lawn care website cost"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      </Helmet>
      <div className="pt-24">
        {/* Hero */}
        <section className="py-12 md:py-16 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <Badge className="mb-4">Pricing Guide</Badge>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
              How much does a lawn care website cost?
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl">
              Real pricing from DIY to done-for-you. No fluff—just the numbers you need to make a smart decision for your lawn care business.
            </p>
            <p className="text-sm text-muted-foreground mt-4">Last updated: {today}</p>
          </div>
        </section>

        {/* Quick Answer Box */}
        <section className="py-12 bg-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <Card className="border-2 border-primary/20">
              <CardContent className="p-6 md:p-8">
                <div className="flex items-center gap-2 mb-6">
                  <DollarSign className="h-6 w-6 text-primary" />
                  <h2 className="text-2xl font-bold">Typical Cost Ranges</h2>
                </div>
                
                <div className="grid gap-4">
                  {/* DIY Builder */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <h3 className="font-semibold">DIY Builder</h3>
                      <p className="text-sm text-muted-foreground">Wix, Squarespace, WordPress.com</p>
                    </div>
                    <div className="mt-2 sm:mt-0 text-right">
                      <p className="font-bold text-lg">$0–$500</p>
                      <p className="text-sm text-muted-foreground">+ $15–$50/mo hosting</p>
                    </div>
                  </div>

                  {/* Freelancer */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <h3 className="font-semibold">Freelancer</h3>
                      <p className="text-sm text-muted-foreground">One-time build, varies widely</p>
                    </div>
                    <div className="mt-2 sm:mt-0 text-right">
                      <p className="font-bold text-lg">$500–$3,000</p>
                      <p className="text-sm text-muted-foreground">+ hosting & maintenance</p>
                    </div>
                  </div>

                  {/* Agency */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <h3 className="font-semibold">Agency</h3>
                      <p className="text-sm text-muted-foreground">Custom design, full service</p>
                    </div>
                    <div className="mt-2 sm:mt-0 text-right">
                      <p className="font-bold text-lg">$3,000–$10,000+</p>
                      <p className="text-sm text-muted-foreground">+ $100–$300/mo ongoing</p>
                    </div>
                  </div>

                  {/* Subscription */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-primary/10 rounded-lg border border-primary/20">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">Subscription (Leaf Rush)</h3>
                        <Badge variant="secondary" className="text-xs">Recommended</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">Everything included, cancel anytime</p>
                    </div>
                    <div className="mt-2 sm:mt-0 text-right">
                      <p className="font-bold text-lg text-primary">$150–$300/mo</p>
                      <p className="text-sm text-muted-foreground">No upfront cost</p>
                    </div>
                  </div>
                </div>

                <p className="text-sm text-muted-foreground mt-6">
                  See our <Link to="/websites" className="text-primary hover:underline">website subscription pricing</Link> for exact numbers.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* What Changes the Price */}
        <section className="py-12 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">What changes the price?</h2>
            <p className="text-muted-foreground mb-8">
              Not all lawn care websites are equal. Here's what drives costs up or down:
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              <Card>
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <FileText className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <h3 className="font-semibold">Number of Services</h3>
                      <p className="text-sm text-muted-foreground">
                        5 services vs 15 services = more <Link to="/service-pages-that-rank/" className="text-primary hover:underline">service pages</Link> to build
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <Search className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <h3 className="font-semibold">Service Areas</h3>
                      <p className="text-sm text-muted-foreground">
                        Each city needs a <Link to="/service-area-pages-local-seo/" className="text-primary hover:underline">service area page</Link> to rank locally
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <Image className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <h3 className="font-semibold">Photos & Galleries</h3>
                      <p className="text-sm text-muted-foreground">
                        Before/after photos, project galleries, team photos add time
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <Settings className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <h3 className="font-semibold">Integrations</h3>
                      <p className="text-sm text-muted-foreground">
                        CRM, scheduling, payment, review widgets add complexity
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <FileText className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <h3 className="font-semibold">Copywriting</h3>
                      <p className="text-sm text-muted-foreground">
                        Professional copy vs DIY writing = better conversions
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <Search className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <h3 className="font-semibold">SEO Setup</h3>
                      <p className="text-sm text-muted-foreground">
                        Schema markup, <Link to="/growth-add-ons/gbp" className="text-primary hover:underline">GBP optimization</Link>, local signals
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* What a Good Lawn Care Website Includes */}
        <section className="py-12 bg-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">What a "good" lawn care website includes</h2>
            <p className="text-muted-foreground mb-8">
              Before comparing prices, know what you're paying for. A <Link to="/lawn-care-website-design/" className="text-primary hover:underline">lawn care website design</Link> that converts should have:
            </p>

            <div className="grid sm:grid-cols-2 gap-3">
              {[
                "Mobile-first design (60%+ of visitors are on phones)",
                "Fast loading (under 3 seconds)",
                "Clear phone number and contact form",
                "Service pages for each offering",
                "Service area pages for local SEO",
                "Before/after photo galleries",
                "Customer reviews/testimonials",
                "Trust signals (license, insurance, years in business)",
                "SSL certificate (https://)",
                "Google Business Profile integration",
                "Schema markup for local search",
                "Analytics tracking"
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <span className="text-sm">{item}</span>
                </div>
              ))}
            </div>

            <p className="text-sm text-muted-foreground mt-6">
              Missing these? Check our <Link to="/resources" className="text-primary hover:underline">resources</Link> for a full website checklist.
            </p>
          </div>
        </section>

        {/* Subscription Model Explained */}
        <section className="py-12 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">Why we use a subscription model</h2>
            
            <div className="space-y-6">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <DollarSign className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">No big upfront cost</h3>
                      <p className="text-muted-foreground">
                        Skip the $3,000–$5,000 lump sum. Start with a predictable monthly payment that fits your cash flow.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <RefreshCw className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Everything included</h3>
                      <p className="text-muted-foreground">
                        Hosting, SSL, updates, edits, support—all bundled. No surprise invoices for "small changes."
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Clock className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Cancel anytime</h3>
                      <p className="text-muted-foreground">
                        No long-term contracts. If it's not working, cancel. We'll even help export your content.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="mt-8 p-4 bg-background rounded-lg border">
              <h3 className="font-semibold mb-2">What's included in our subscription:</h3>
              <ul className="grid sm:grid-cols-2 gap-2 text-sm">
                {[
                  "Custom design (not a template)",
                  "Hosting & SSL",
                  "Mobile optimization",
                  "Service pages",
                  "Service area pages",
                  "Contact forms",
                  "Unlimited edits",
                  "Ongoing support",
                  "SEO-ready structure",
                  "Speed optimization"
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-primary" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-12 bg-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">Quick comparison</h2>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-2 font-semibold">Feature</th>
                    <th className="text-center py-3 px-2 font-semibold">DIY</th>
                    <th className="text-center py-3 px-2 font-semibold">Freelancer</th>
                    <th className="text-center py-3 px-2 font-semibold">Agency</th>
                    <th className="text-center py-3 px-2 font-semibold bg-primary/5">Subscription</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="py-3 px-2">Upfront cost</td>
                    <td className="text-center py-3 px-2">$0–$500</td>
                    <td className="text-center py-3 px-2">$500–$3K</td>
                    <td className="text-center py-3 px-2">$3K–$10K</td>
                    <td className="text-center py-3 px-2 bg-primary/5 font-medium">$0</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-3 px-2">Monthly cost</td>
                    <td className="text-center py-3 px-2">$15–$50</td>
                    <td className="text-center py-3 px-2">$50–$150</td>
                    <td className="text-center py-3 px-2">$100–$300</td>
                    <td className="text-center py-3 px-2 bg-primary/5 font-medium">$150–$300</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-3 px-2">Time to launch</td>
                    <td className="text-center py-3 px-2">Weeks</td>
                    <td className="text-center py-3 px-2">2–4 weeks</td>
                    <td className="text-center py-3 px-2">4–8 weeks</td>
                    <td className="text-center py-3 px-2 bg-primary/5 font-medium">2 weeks</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-3 px-2">Edits included</td>
                    <td className="text-center py-3 px-2"><X className="h-4 w-4 text-muted-foreground mx-auto" /></td>
                    <td className="text-center py-3 px-2">Limited</td>
                    <td className="text-center py-3 px-2">Limited</td>
                    <td className="text-center py-3 px-2 bg-primary/5"><Check className="h-4 w-4 text-primary mx-auto" /></td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-3 px-2">Hosting included</td>
                    <td className="text-center py-3 px-2"><X className="h-4 w-4 text-muted-foreground mx-auto" /></td>
                    <td className="text-center py-3 px-2"><X className="h-4 w-4 text-muted-foreground mx-auto" /></td>
                    <td className="text-center py-3 px-2">Sometimes</td>
                    <td className="text-center py-3 px-2 bg-primary/5"><Check className="h-4 w-4 text-primary mx-auto" /></td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-3 px-2">Support</td>
                    <td className="text-center py-3 px-2">DIY</td>
                    <td className="text-center py-3 px-2">Email</td>
                    <td className="text-center py-3 px-2">Dedicated</td>
                    <td className="text-center py-3 px-2 bg-primary/5 font-medium">Ongoing</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-2">Lawn care expertise</td>
                    <td className="text-center py-3 px-2"><X className="h-4 w-4 text-muted-foreground mx-auto" /></td>
                    <td className="text-center py-3 px-2">Varies</td>
                    <td className="text-center py-3 px-2">Varies</td>
                    <td className="text-center py-3 px-2 bg-primary/5"><Check className="h-4 w-4 text-primary mx-auto" /></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-12 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-8">Frequently asked questions</h2>
            
            <Accordion type="single" collapsible className="space-y-4">
              {faqItems.map((item, index) => (
                <AccordionItem 
                  key={index} 
                  value={`faq-${index}`}
                  className="bg-background rounded-lg border px-4"
                >
                  <AccordionTrigger className="text-left hover:no-underline py-4">
                    <span className="font-medium">{item.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-4">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-12 bg-primary/5">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Ready to get a real quote?</h2>
            <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
              Stop guessing. Book a 15-minute call to discuss your lawn care business and get exact pricing.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" asChild>
                <a href="#get-started">
                  <Calendar className="mr-2 h-5 w-5" />
                  Get Started
                </a>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/websites">
                  See Website Subscription Pricing
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Related Links */}
        <section className="py-8 bg-background border-t">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm text-muted-foreground mb-4">Related reading:</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/lawn-care-website-design/" className="text-sm text-primary hover:underline">Lawn Care Website Design</Link>
              <span className="text-muted-foreground">•</span>
              <Link to="/service-pages-that-rank/" className="text-sm text-primary hover:underline">Service Pages That Rank</Link>
              <span className="text-muted-foreground">•</span>
              <Link to="/service-area-pages-local-seo/" className="text-sm text-primary hover:underline">Service Area Pages</Link>
              <span className="text-muted-foreground">•</span>
              <Link to="/contact" className="text-sm text-primary hover:underline">Contact Us</Link>
            </div>
          </div>
        </section>

        {/* Calendly */}
        <section id="schedule-call" className="py-12 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl font-bold text-center mb-8">Schedule your free consultation</h2>
            <CalendlyEmbed />
          </div>
        </section>
      </div>
    </>
  );
};

export default LawnCareWebsiteCost;
